/**
 * Task 26: Password Reset Page
 * Forces password change on first login or when must_change_password flag is set
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import axios from '../config/axiosConfig';

const PasswordResetPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [policyErrors, setPolicyErrors] = useState([]);

  // Check for valid session on mount
  useEffect(() => {
    const token = localStorage.getItem('spes_token');
    if (!token) {
      setError('Your session has expired. Please log in again.');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    }
  }, [navigate]);

  // Password policy validation
  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 8) {
      errors.push('At least 8 characters');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('One uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('One lowercase letter');
    }
    if (!/\d/.test(password)) {
      errors.push('One number');
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};:'",.<>?/\\|`~]/.test(password)) {
      errors.push('One special character');
    }
    
    return errors;
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setFormData({ ...formData, newPassword });
    setPolicyErrors(validatePassword(newPassword));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password policy
    const errors = validatePassword(formData.newPassword);
    if (errors.length > 0) {
      setError('Password does not meet security requirements');
      return;
    }

    setLoading(true);

    try {
      await axios.post('/users/reset-password', {
        new_password: formData.newPassword
      });

      // Success - redirect to dashboard
      const userRole = localStorage.getItem('spes_role');
      if (userRole === 'admin') {
        navigate('/admin-dashboard');
      } else if (userRole === 'bm' || userRole === 'am' || userRole === 'tl') {
        navigate('/manager-dashboard');
      } else {
        navigate('/recruiter-dashboard');
      }
    } catch (err) {
      console.error('Password reset error:', err);
      
      // Handle 403 Forbidden (session expired)
      if (err.response?.status === 403) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setError(err.response?.data?.detail || 'Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const PolicyCheck = ({ met, text }) => (
    <div className="flex items-center gap-2 text-sm">
      {met ? (
        <CheckCircle className="w-4 h-4 text-green-500 dark:text-green-400" />
      ) : (
        <XCircle className="w-4 h-4 text-red-400 dark:text-red-500" />
      )}
      <span className={met ? 'text-green-600 dark:text-green-300' : 'text-gray-700 dark:text-gray-200'}>{text}</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-primary/5 to-background flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-teal to-gold rounded-full mb-4">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 font-heading mb-2">
            Reset Your Password
          </h1>
          <p className="text-gray-600 dark:text-gray-300 font-body">
            For security, please create a new password
          </p>
        </div>

        {/* Warning Notice */}
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-yellow-800 dark:text-yellow-200">
            <p className="font-semibold mb-1">Password Change Required</p>
            <p>You must change your password before accessing the system.</p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white dark:bg-navy-dark rounded-xl shadow-lg p-8 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* New Password */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2 font-body">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.newPassword}
                onChange={handlePasswordChange}
                className="w-full px-4 py-3 pr-12 bg-gray-50 dark:bg-navy border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal focus:border-transparent transition font-body text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                placeholder="Enter new password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Password Policy Checklist */}
          <div className="bg-gray-50 dark:bg-navy rounded-lg p-4 space-y-2">
            <p className="text-sm font-semibold text-gray-900 dark:text-white mb-3 font-body">
              Password Requirements:
            </p>
            <PolicyCheck 
              met={formData.newPassword.length >= 8} 
              text="At least 8 characters" 
            />
            <PolicyCheck 
              met={/[A-Z]/.test(formData.newPassword)} 
              text="One uppercase letter (A-Z)" 
            />
            <PolicyCheck 
              met={/[a-z]/.test(formData.newPassword)} 
              text="One lowercase letter (a-z)" 
            />
            <PolicyCheck 
              met={/\d/.test(formData.newPassword)} 
              text="One number (0-9)" 
            />
            <PolicyCheck 
              met={/[!@#$%^&*()_+\-=\[\]{};:'",.<>?/\\|`~]/.test(formData.newPassword)} 
              text="One special character (!@#$%...)" 
            />
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2 font-body">
              Confirm New Password
            </label>
            <div className="relative">
              <input
                type={showConfirm ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="w-full px-4 py-3 pr-12 bg-gray-50 dark:bg-navy border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal focus:border-transparent transition font-body text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                placeholder="Re-enter new password"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
              <p className="text-red-500 dark:text-red-400 text-sm mt-2 flex items-center gap-1">
                <XCircle className="w-4 h-4" />
                Passwords do not match
              </p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || policyErrors.length > 0 || formData.newPassword !== formData.confirmPassword}
            className="w-full bg-gradient-to-r from-teal to-gold text-white font-semibold py-3 rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed font-body"
          >
            {loading ? 'Updating Password...' : 'Update Password & Continue'}
          </button>
        </form>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6 font-body">
          Spes Recruiter Portal v26.0
        </p>
      </div>
    </div>
  );
};

export default PasswordResetPage;
